<template>
  <div>
    <v-text-field
      :label="label"
      :disabled="disabled"
      :value="selectedText"
      placeholder="none"
      readonly
      class="allergy-field"
      append-icon="mdi-playlist-edit"
      @click="open"
      @click:append="open"
    />
    <div class="mt-n2">
      <div v-for="restriction of selectedRestrictions" v-bind:key="restriction.id" class="mb-1">
        <!-- the per-restriction settings (users/<uid>.allergy_settings): both
             on = "we'll adjust it" (the default, no entry stored); off =
             "avoid it" — the menu never picks the meal / the kitchen leaves
             the ingredient in and the label says "contains …" -->
        <div v-if="settingsEnabled" class="d-flex align-center flex-wrap ml-1 mb-1">
          <small class="grey--text text--darken-1 mr-3">{{ restriction.text }}:</small>
          <v-checkbox
            dense hide-details
            class="mt-0 mr-4 allergy-setting"
            label="in personal menu"
            :disabled="disabled"
            :input-value="settingFor(restriction.id).includeInMenu"
            @change="setSetting(restriction.id, 'includeInMenu', $event)"
          />
          <v-checkbox
            dense hide-details
            class="mt-0 allergy-setting"
            label="substitute ingredient"
            :disabled="disabled"
            :input-value="settingFor(restriction.id).substituteIngredient"
            @change="setSetting(restriction.id, 'substituteIngredient', $event)"
          />
          <small v-if="!settingFor(restriction.id).substituteIngredient" class="orange--text text--darken-2 ml-2">
            delivered as-is, label says "contains"
          </small>
        </div>
        <small class="grey--text text--darken-1">{{ restriction.text }} matches tags:</small>
        <template v-if="restriction.tags.length">
          <v-chip
            v-for="tag of restriction.tags"
            v-bind:key="`${restriction.id}-${tag}`"
            x-small
            outlined
            class="ml-1"
          >
            {{ tag }}
          </v-chip>
        </template>
        <small v-else class="grey--text ml-1">no tags</small>
        <restriction-stats v-if="restriction.stats" :stats="restriction.stats"/>
      </div>
    </div>

    <v-dialog v-model="showDialog" max-width="600" scrollable>
      <v-card>
        <v-card-title>{{ label }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="search"
            label="search by allergy or tag"
            prepend-inner-icon="mdi-magnify"
            clearable
            autofocus
            hide-details
          />
          <div class="d-flex align-center flex-wrap">
            <v-checkbox
              v-model="showInactive"
              label="show inactive"
              hint="not ordered with in the last month"
              persistent-hint
              dense
              class="mr-6 mt-2"
            />
            <v-checkbox
              v-model="showUnused"
              label="show unused"
              hint="no customer has it on their profile"
              persistent-hint
              dense
              class="mt-2"
            />
            <v-spacer/>
            <small v-if="hiddenCount" class="grey--text">{{ hiddenCount }} hidden</small>
          </div>
          <v-list>
            <v-list-item
              v-for="option of matchingOptions"
              v-bind:key="option.value"
              @click="toggle(option.value)"
            >
              <v-list-item-action>
                <v-simple-checkbox
                  :value="isChosen(option.value)"
                  :ripple="false"
                  @input="toggle(option.value)"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ option.text }}
                  <v-chip
                    v-for="badge of option.badges"
                    v-bind:key="`${option.value}-${badge.text}`"
                    :color="badge.color"
                    x-small
                    outlined
                    class="ml-1"
                  >
                    {{ badge.text }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle v-if="option.tags.length" class="tag-list">
                  matches tags {{ option.tags.join(', ') }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="option.stats">
                  <restriction-stats :stats="option.stats"/>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="!matchingOptions.length">
              <v-list-item-content>
                <v-list-item-title class="grey--text">{{ noMatchesText }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <small class="grey--text ml-2">{{ chosen.length }} selected</small>
          <v-spacer/>
          <v-btn text @click="showDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="apply">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.tag-list {
  white-space: normal;
}

/* the field only opens the dialog, so don't let it look like somewhere to type */
.allergy-field >>> input {
  cursor: pointer;
}

.allergy-setting >>> .v-label {
  font-size: 12px;
}
</style>

<script>
import moment from "moment";
import {mapActions} from "vuex";
import RestrictionStats from "@/components/RestrictionStats";

// how recently a restriction must have been ordered with to count as active
const ACTIVE_SINCE = () => moment().subtract(1, 'month');

export default {
  name: "AllergySelect",
  components: {RestrictionStats},
  props: {
    value: {type: [Array, String], required: false, default: () => []},
    label: {type: String, required: true},
    disabled: {type: Boolean, required: false, default: false},
    // the per-restriction settings beside `value` (allergy_settings): a map
    // by restriction id; `false` (the pending-edit "clear it"), null or
    // undefined all mean none. The settings row shows when the parent listens
    // for `update:settings`, which emits the map pruned to the selected
    // restrictions, entries at the defaults dropped, an empty map as `false`
    settings: {type: [Object, Boolean], required: false, default: undefined}
  },
  mounted() {
    return Promise.all([this.fetchRestrictions(), this.fetchFoodTags(), this.fetchRestrictionStats()]);
  },
  data() {
    return {
      showDialog: false,
      search: '',
      // the list is long and mostly dormant, so quiet restrictions stay out of the way until asked
      // for - kept between openings, unlike the search, since it is a view preference
      showInactive: false,
      showUnused: false,
      // what is ticked in the dialog, only sent back to the customer on Done
      chosen: []
    }
  },
  methods: {
    ...mapActions(['fetchRestrictions', 'fetchFoodTags', 'fetchRestrictionStats']),
    open() {
      if (this.disabled) return;
      this.search = '';
      this.chosen = [...this.selected];
      this.showDialog = true;
    },
    isChosen(id) {
      return this.chosen.includes(id);
    },
    toggle(id) {
      this.chosen = this.isChosen(id)
        ? this.chosen.filter(chosen => chosen !== id)
        : [...this.chosen, id];
    },
    apply() {
      this.$emit('input', this.chosen);
      if (this.settingsEnabled) {
        // a restriction that was removed takes its settings with it
        this.emitSettings(this.prunedSettings(this.chosen));
      }
      this.showDialog = false;
    },
    // the effective settings of a restriction: the stored entry over the
    // defaults (both true) — fed-lib restrictions-client allergySettingsFor
    settingFor(id) {
      const stored = this.settings && typeof this.settings === 'object' ? this.settings[id] : null;
      return {
        includeInMenu: !(stored && stored.includeInMenu === false),
        substituteIngredient: !(stored && stored.substituteIngredient === false)
      };
    },
    isDefaultSetting({includeInMenu, substituteIngredient}) {
      return includeInMenu && substituteIngredient;
    },
    prunedSettings(ids) {
      const out = {};
      ids.forEach(id => {
        const setting = this.settingFor(id);
        if (!this.isDefaultSetting(setting)) out[id] = setting;
      });
      return out;
    },
    setSetting(id, key, value) {
      const next = this.prunedSettings(this.selected);
      const entry = {...this.settingFor(id), [key]: !!value};
      if (this.isDefaultSetting(entry)) {
        delete next[id];
      } else {
        next[id] = entry;
      }
      this.emitSettings(next);
    },
    emitSettings(map) {
      this.$emit('update:settings', Object.keys(map).length ? map : false);
    },
    // the food tags a restriction makes us avoid, eg no-sugar -> has-sugar
    tagNames(restriction) {
      return (restriction.restrictedTags || []).map(tagId => this.foodTagNames[tagId] || tagId);
    },
    // how much this restriction is used, null until the weekly stats job has recorded it
    statsFor(id) {
      const stats = this.$store.state.restrictionStats;
      return (stats && stats[id] && stats[id].stats) || null;
    },
    // no customer has it on their profile right now, ordering or not - the same count the row
    // shows, so the chip cannot contradict the number beside it
    isUnused({stats}) {
      return !stats || !stats.currentCustomerCount;
    },
    // gone quiet: never ordered with, or not since ACTIVE_SINCE. a restriction with no stats at all
    // has not been recorded yet, so it counts as inactive too
    isInactive({stats}) {
      const lastUsedWeek = stats && stats.lastUsedWeek;
      return !lastUsedWeek || moment(lastUsedWeek).isBefore(ACTIVE_SINCE());
    },
    // the two say different things - nobody has it now, versus nobody has ordered with it lately -
    // and either can be true without the other, so both are shown when both apply
    activityBadges(option) {
      const badges = [];
      if (this.isInactive(option)) badges.push({text: 'inactive', color: 'orange darken-2'});
      if (this.isUnused(option)) badges.push({text: 'unused', color: 'blue-grey'});
      return badges;
    },
    // restrictions in ordinary use always show; each toggle lets its own quiet group back in, so
    // ticking one shows those whether or not the other applies as well. anything the customer
    // already has stays visible whatever the filters say, so it can still be unticked
    isVisible(option) {
      if (this.isChosen(option.value)) return true;
      if (this.showUnused && this.isUnused(option)) return true;
      if (this.showInactive && this.isInactive(option)) return true;
      return !this.isUnused(option) && !this.isInactive(option);
    },
    // the tags are on show in the dialog, so let them be searched for too, but an allergy named
    // like the search is the more likely thing to be after
    matchingIn(options) {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return options;
      const matchesName = option => option.text.toLowerCase().includes(query);
      const matchesTag = option => option.tags.some(tag => String(tag).toLowerCase().includes(query));
      return [
        ...options.filter(matchesName),
        ...options.filter(option => !matchesName(option) && matchesTag(option))
      ];
    }
  },
  computed: {
    settingsEnabled() {
      return !!this.$listeners['update:settings'];
    },
    selected() {
      return Array.isArray(this.value)
        ? this.value
        : (this.value || '').split(',').map(a => a.trim()).filter(a => a);
    },
    selectedText() {
      return this.selectedRestrictions.map(({text}) => text).join(', ');
    },
    foodTagNames() {
      return Object.values(this.$store.state.food_tags || {})
        .reduce((names, {id, name}) => {
          names[id] = name;
          return names;
        }, {});
    },
    options() {
      const selected = new Set(this.selected);
      // the store keys restrictions by id and also by each restricted tag id - only the former have an id
      return Object.values(this.$store.state.restrictions || {})
        .filter(({id}) => id)
        .map(restriction => {
          const option = {
            value: restriction.id,
            text: restriction.text,
            tags: this.tagNames(restriction),
            stats: this.statsFor(restriction.id)
          };
          return {...option, badges: this.activityBadges(option)};
        })
        .sort((a, b) => {
          // what the customer already has goes to the top, on the saved list rather than what is
          // ticked, so rows do not jump around underneath as they are ticked and unticked
          const bySelected = (selected.has(b.value) ? 1 : 0) - (selected.has(a.value) ? 1 : 0);
          return bySelected || a.text.toLowerCase().localeCompare(b.text.toLowerCase());
        });
    },
    visibleOptions() {
      return this.options.filter(option => this.isVisible(option));
    },
    hiddenCount() {
      return this.options.length - this.visibleOptions.length;
    },
    // the search runs over what the filters left, so when it comes up empty say whether the answer
    // is being held back by a filter rather than letting it look like there is no such allergy
    noMatchesText() {
      const query = (this.search || '').trim();
      const hidden = this.matchingIn(this.options).filter(option => !this.isVisible(option));
      if (!hidden.length) {
        return query ? `no allergy or tag matches "${query}"` : 'no allergies to show';
      }
      // a hidden restriction can be inactive, unused, or both, so count them once between the two
      const matching = query ? ` matching "${query}"` : '';
      return `${hidden.length} inactive or unused${matching} - use the filters above to show them`;
    },
    matchingOptions() {
      return this.matchingIn(this.visibleOptions);
    },
    selectedRestrictions() {
      const restrictions = this.$store.state.restrictions || {};
      return this.selected.map(id => {
        const restriction = restrictions[id] || {};
        return {
          id,
          text: restriction.text || id,
          tags: this.tagNames(restriction),
          stats: this.statsFor(id)
        };
      });
    }
  }
}
</script>
